import { getQuerySession } from "@/api/query-client";
import { Actions, AppContext, UnityBridge } from "@Context/common/general/context";
import CRUDActions from "@Enums/CrudActions";
import Preferences from "@Enums/Preferences";
import useUserPreferences from "@hooks/common/user/use-user-preferences";
import type { UserPreferences } from "@Shared/schemas/users/userSchemas";
import { useContext, useEffect, useRef } from "react";
import { getChangedPreferences } from "./preferences-service.utils";

interface IPreferencesSnapshot {
  session: number;
  preferences: UserPreferences | undefined;
}

const PreferencesService = () => {
  const { dispatch } = useContext(AppContext);
  const { preferences } = useUserPreferences();
  const session = getQuerySession();
  const lastSnapshot = useRef<IPreferencesSnapshot>({ session, preferences: undefined });

  useEffect(() => {
    if (lastSnapshot.current.session !== session) {
      lastSnapshot.current = { session, preferences: undefined };
    }
    if (!preferences) return;
    // Compatibility projection for older consumers. Query owns the fetched data.
    dispatch({
      type: Actions.CoreActions.UpdateState,
      payload: {
        action: CRUDActions.UPDATE,
        preferences: Object.fromEntries(Object.keys(Preferences).map(key => [key, !!preferences[key]])),
      },
    });
    const changes = getChangedPreferences(lastSnapshot.current.preferences, preferences);
    lastSnapshot.current = { session, preferences: { ...preferences } };
    if (Object.keys(changes).length) {
      UnityBridge.dispatchWebEvent({
        type: UnityBridge.OutgoingWebEvents.SetPreferences,
        payload: changes,
      });
    }
  }, [preferences, session]);

  return null;
};

export default PreferencesService;
